import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import axios from 'axios';

import Data from '../../Data/Data.json';
import { Crypter } from '../../Functions/Security/Crypter';



export interface HttpOnlySessionUser {

  coduti?: string;

  nomuti?: string;

  codage?: string;

  agence?: string;

  codgroup?: string;

  group?: string;

  datjour?: string;

  CaisseUser?: string;

  Role?: string;

  role?: string;

  Authenticated?: boolean;

  MfaVerified?: boolean;

  ExpiresAt?: string;

  [key: string]: unknown;

}



interface SessionContextValue {

  user: HttpOnlySessionUser | null;

  loading: boolean;

  refreshSession: () => Promise<boolean>;

  signOut: () => Promise<void>;

  signIn: (payload?: { authState?: HttpOnlySessionUser }) => boolean;

}


const baseUrl = Data.ApiUrl.BaseUrl;

const sessionEndpoint = `${Data.ApiUrl.authentification}/session`;

const logoutEndpoint = `${Data.ApiUrl.authentification}/logout`;

const SessionContext = createContext<SessionContextValue | null>(null);



const logAuth = (message: string, details?: Record<string, unknown>) => {

  console.info(`[AUTH] ${message}`, details ?? '');

};



function decodeSession(response: any): HttpOnlySessionUser | null {

  try {

    const raw = typeof response?.data === 'string' ? response.data : JSON.stringify(response?.data);

    const decoded = Crypter.decrypt(raw);

    const parsed = JSON.parse(decoded);

    const value = parsed?.Value ?? parsed;

    const session = value?.Result ?? value;

    return normalizeSession(session);

  } catch (err) {

    logAuth('decodeSession failed, fallback to raw', { error: String(err), dataType: typeof response?.data });

    const value = response?.data?.Value ?? response?.data;

    const session = value?.Result ?? value;

    return normalizeSession(session);

  }

}



function parseBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return ['true', '1', 'yes', 'oui'].includes(value.trim().toLowerCase());
  return false;
}

function normalizeSession(session: any): HttpOnlySessionUser | null {

  if (!session || typeof session !== 'object') {
    return null;
  }

  logAuth('normalizeSession raw', { keys: Object.keys(session), authenticated: session?.Authenticated, mfaVerified: session?.MfaVerified });

  const isAuthenticated = parseBool(session?.Authenticated ?? session?.authenticated);

  if (!isAuthenticated) {

    return null;

  }



  const normalized = {

    ...session,

    Authenticated: true,

    coduti: session.coduti ?? session.CodUti ?? session.CODUTI,

    nomuti: session.nomuti ?? session.NomUti ?? session.NOMUTI,

    codage: session.codage ?? session.Codage ?? session.CODAGE,

    agence: session.agence ?? session.Agence ?? session.AGENCE,

    role: String(session.role ?? session.Role ?? '').trim(),

    codgroup: session.codgroup ?? session.CodGroup ?? session.CODGROUP ?? session.Role?.trim(),

    group: session.group ?? session.Group ?? session.GROUP ?? session.Role?.trim(),

    datjour: session.datjour ?? session.DatJour ?? session.DATJOUR,

    CaisseUser: session.CaisseUser ?? session.CAISSEUSER,

    MfaVerified: parseBool(session.MfaVerified ?? session.mfaVerified ?? session.mfa_verified),

  };


  return normalized;

}



export function SessionProvider({ children }: { children: React.ReactNode }) {

  const [user, setUser] = useState<HttpOnlySessionUser | null>(null);

  const [loading, setLoading] = useState(true);



  const refreshSession = async () => {

    logAuth('GET /Authentification/session start', {

      endpoint: sessionEndpoint,

      withCredentials: true,

    });

    try {

      const response = await axios.get(sessionEndpoint, { withCredentials: true });

      const sessionUser = decodeSession(response);

      setUser((currentUser) => {

        if (!currentUser || !sessionUser) return sessionUser;



        const mergedUser = { ...currentUser };

        Object.entries(sessionUser).forEach(([key, value]) => {

          if (value !== null && value !== undefined && value !== '') {

            mergedUser[key] = value;

          }

        });

        return mergedUser;

      });

      logAuth('GET /Authentification/session response', {

        httpStatus: response.status,

        authenticated: Boolean(sessionUser),

        userCode: sessionUser?.CodUti ?? sessionUser?.coduti ?? null,

        role: sessionUser?.Role ?? null,

        mfaVerified: sessionUser?.MfaVerified ?? null,

      });

      return Boolean(sessionUser);

    } catch (error: any) {

      logAuth('GET /Authentification/session failed', {

        httpStatus: error?.response?.status ?? null,

        responseDataType: typeof error?.response?.data,

        responseData: error?.response?.data ? String(error.response.data).slice(0, 500) : null,

        networkRequestCreated: Boolean(error?.request),

        message: error?.message ?? 'unknown error',

        code: error?.code ?? null,

      });

      setUser(null);

      return false;

    } finally {

      setLoading(false);

    }

  };



  const signOut = async () => {

    try {

      await axios.post(logoutEndpoint, undefined, { withCredentials: true });

    } finally {

      setUser(null);

    }

  };



  const signIn = (payload?: { authState?: HttpOnlySessionUser }) => {

    if (payload?.authState) setUser(payload.authState);

    return true;

  };



  useEffect(() => {

    const handleSessionExpired = () => setUser(null);

    window.addEventListener('auth:expired', handleSessionExpired);



    if (window.location.pathname.toLowerCase() !== '/login') {

      refreshSession();

    } else {

      setLoading(false);

    }



    return () => window.removeEventListener('auth:expired', handleSessionExpired);

  }, []);



  const value = useMemo(() => ({ user, loading, refreshSession, signOut, signIn }), [user, loading]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;

}



function useSessionContext() {

  const context = useContext(SessionContext);

  if (!context) throw new Error('SessionProvider is required.');

  return context;

}



export function useSession() {

  return useSessionContext();

}



export function useAuthUser() {

  const { user } = useSessionContext();

  return useCallback(() => user, [user]);

}



export function useAuthHeader() {

  return useCallback(() => '', []);

}



export function useSignIn() {

  return useSessionContext().signIn;

}



export function useSignOut() {

  return useSessionContext().signOut;

}



export function useIsAuthenticated() {

  const { user, loading } = useSessionContext();

  return () => !loading && Boolean(user);

}



export function RequireAuth({ 
  children, 
  loginPath = '/Login', 
  allowUnverifiedMfa = false 
}: { 
  children: React.ReactNode; 
  loginPath?: string; 
  allowUnverifiedMfa?: boolean; 
}) {
  const { user, loading } = useSessionContext();

  if (loading) return null;

  // 1. Non authentifié -> redirection /Login
  if (!user || user.Authenticated !== true) {
    window.location.replace(loginPath);
    return null;
  }

  // 2. MFA NON VÉRIFIÉ (mfa_verified === false)
  if (user.MfaVerified === false) {
    const rawPath = window.location.pathname.toLowerCase().trim();
    const currentPath = rawPath.replace(/\/+$/, '') || '/';
    
    const requiresSetup = sessionStorage.getItem('requiresSetup') === 'true';
    const targetMfaPath = requiresSetup ? '/mfa-activation' : '/mfa-verify';

    // Règle de sécurité absolue :
    // L'utilisateur N'A ACCÈS qu'à targetMfaPath UNIQUEMENT (correspondance EXACTE).
    // Si l'enfant n'est pas une page MFA (ex: Home sur /*) OU si l'URL dans le navigateur n'est pas EXACTEMENT targetMfaPath :
    if (!allowUnverifiedMfa || currentPath !== targetMfaPath) {
      logAuth('MFA Guard: session unverified. Blocking access and enforcing redirect', {
        from: window.location.pathname,
        to: targetMfaPath,
        requiresSetup,
      });
      window.location.replace(targetMfaPath);
      return null;
    }

    // Autoriser UNIQUEMENT l'affichage de la page MFA spécifique
    return <>{children}</>;
  }

  // 3. MFA DÉJÀ VÉRIFIÉ (mfa_verified === true)
  // Si l'utilisateur pleinement authentifié tente d'aller sur /mfa-activation ou /mfa-verify :
  if (user.MfaVerified === true && allowUnverifiedMfa) {
    window.location.replace('/');
    return null;
  }

  // 4. Utilisateur pleinement authentifié sur une route standard de l'application
  return <>{children}</>;
}



export function AuthProvider({ children }: { children: React.ReactNode }) {

  return <SessionProvider>{children}</SessionProvider>;

}